import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { TFunction } from "i18next";
import type { Form, FormValidator } from "../types";
import makeReducer from "./reducer";
import { Logger } from "@/utils/logging";

const log = new Logger("useForm");

export type FormParams<Keys extends string> = {
  initialValues?: Record<Keys, string>;
  initialErrors?: Record<Keys, string | false>;
  validate: FormValidator<Keys>;
};

export const useFormErrorHelper = (state: Form<any>["state"], t: TFunction) =>
  useCallback(
    (field: string, params: Record<string, string | number> = {}) =>
      state.touched[field] &&
      state.errors[field] &&
      // Removing the `as string` will cause a type error because the error is a string | false.
      // This is an issue with TS, we know that this is a string at this point in execution.
      t(state.errors[field] as string, params),
    [state, t]
  );

// This is a custom hook to manage form state like values, errors, and touched fields.
// It's needed because Spreedly uses an async validation process through an iframe.
// We have to allow errors to be set from outside of the validator in this hook.
export const useForm = <Keys extends string>({
  validate,
  initialValues,
  initialErrors,
}: FormParams<Keys>): Form<Keys> => {
  const [state, dispatch] = useReducer(
    makeReducer({ validate, initialValues, initialErrors }),
    {
      isValid: false,
      values: initialValues || {},
      errors: initialErrors || {},
      touched: {},
    }
  );

  // const { t } = useI18nContext();

  useEffect(() => {
    // Initialize the form state when the component mounts.
    // This will run the validation function and set the initial state.
    dispatch({ type: "initialize" });
  }, []);

  const onFieldChange = useCallback((name: Keys, value: string) => {
    log.verbose("onFieldChange", name, value);
    dispatch({ type: "change", name, value });
  }, []);

  const onFieldBlur = useCallback(
    (name: Keys) => dispatch({ type: "blur", name }),
    []
  );

  const onFieldError = useCallback(
    (name: Keys, error: string | false) =>
      dispatch({ type: "error", name, error }),
    []
  );

  const setErrors = useCallback(
    (errors: Record<Keys, string | false>) =>
      dispatch({ type: "setErrors", errors }),
    []
  );

  const performValidation = useCallback(
    () => dispatch({ type: "validate" }),
    []
  );

  const helpers = useMemo(
    () => ({
      onFieldChange,
      onFieldBlur,
      onFieldError,
      setErrors,
      performValidation,
    }),
    [onFieldChange, onFieldBlur, onFieldError, setErrors, performValidation]
  );

  return {
    state,
    helpers,
  };
};
